import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import type { IRouteMetadata } from '@/modules/Core/interfaces'
import { validatePermiso, validateRole } from '@/modules/Core/usings/generales'
import { BaseService } from '@/modules/Core/services/BaseService'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Paginas de error accesibles sin sesion (la de mantenimiento/acceso denegado
// se muestran incluso sin token valido)
const RUTAS_ERROR = ['/error403', '/error503']

router.beforeEach((to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title)

  if (nearestWithTitle && typeof nearestWithTitle.meta.title === 'string') {
    document.title = nearestWithTitle.meta.title
  }

  const meta = (nearestWithTitle?.meta ?? {}) as unknown as IRouteMetadata

  // Limpia nodos inyectados por navegaciones previas
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach(
    (el: Element) => el.parentNode?.removeChild(el)
  )

  // Una ruta es publica si declara requiresSession:false (login, recuperar,
  // solicitud) o es una pagina de error
  const esPublica =
    meta.requiresSession === false || RUTAS_ERROR.includes(to.path)

  // --- 1. Control de sesion (preventivo) ---
  // Se ejecuta SIEMPRE en el guard: si la ruta necesita sesion y no hay token,
  // se limpian cookies y se redirige al login antes de montar la vista.
  if (!esPublica) {
    const cookies = BaseService.cookies
    if (!cookies || !cookies.get('token')) {
      cookies?.keys().forEach((cookie: string) => cookies.remove(cookie))
      return next('/login')
    }
  }

  // --- 2. Control de acceso (permisos / perfiles) ---
  // La ruta puede declarar `permiso`, `perfiles` o ambos. El acceso se concede
  // si el usuario satisface AL MENOS UNO de los requisitos declarados; si no
  // declara ninguno, queda abierta (solo protegida por sesion).
  if (!esPublica) {
    const requisitos: boolean[] = []
    if (meta.permiso && meta.permiso.length > 0) {
      requisitos.push(validatePermiso(meta.permiso))
    }
    if (meta.perfiles && meta.perfiles.length > 0) {
      requisitos.push(validateRole(meta.perfiles))
    }
    const tieneAcceso = requisitos.length === 0 || requisitos.some(Boolean)
    if (!tieneAcceso) {
      return next('/error403')
    }
  }

  next()
})

router.afterEach((to, from, failure) => {
  if (!failure) {
    setTimeout(() => {
      window.HSStaticMethods.autoInit()
    }, 100)
  }
})

export default router
